import React, { useEffect, useRef } from 'react';
import { ConsultingLayout } from '../layouts/ConsultingLayout';
import { CheckCircle2, Clock, Video, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../components/legal';
import { Navigation } from '../components/Navigation';

const CALENDLY_URL = 'https://calendly.com/nico-helfrich-koenigswege/30min';

const StrategySession = () => {
  const navigate = useNavigate();
  const calendlyContainerRef = useRef(null);

  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <ConsultingLayout>
      <Navigation variant="light" />

      <section className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 pt-28 pb-20">
        <div className="mb-10">
          <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-navy mb-4">
            Erstgespräch vereinbaren
          </h1>
          <p className="font-playfair text-xl sm:text-2xl text-navy/80 leading-relaxed mb-4">
            Wo willst du hin, was brauchst du dafür und wie setzt man das sauber um?
          </p>
          <p className="text-sm sm:text-base text-charcoal leading-relaxed">
            In einem unverbindlichen Gespräch lernen wir uns kennen. Mir geht es zuerst darum, zu verstehen, wo du stehst und wo du hinwillst, bevor wir über mögliche Lösungen sprechen.
          </p>
        </div>

        <div className="bg-white rounded-xl p-5 sm:p-6 border border-black/5 shadow-sm mb-8">
          <h2 className="font-outfit text-lg font-bold text-navy mb-4">Was dich erwartet</h2>
          <div className="space-y-4">
            {[
              { icon: CheckCircle2, title: 'Kennenlernen', desc: 'Wer bist du, was machst du, wie sieht deine Lebenssituation aus?' },
              { icon: CheckCircle2, title: 'Deine Ziele verstehen', desc: 'Wo willst du hin? Was ist dir wichtig, was beschäftigt dich?' },
              { icon: CheckCircle2, title: 'Ehrliche Einschätzung', desc: 'Ich sage dir, ob und wie eine tiefere Analyse für dich Sinn macht.' }
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-7 h-7 bg-cobalt/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-4 h-4 text-cobalt" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="font-medium text-navy text-sm mb-0.5">{item.title}</h3>
                  <p className="text-sm text-charcoal leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-8">
          <div className="bg-white rounded-lg p-4 border border-black/5 text-center">
            <Clock className="w-6 h-6 text-navy mx-auto mb-2" strokeWidth={1.5} />
            <div className="font-medium text-navy text-sm mb-0.5">Dauer</div>
            <div className="text-xs text-slate">30 Minuten</div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-black/5 text-center">
            <Video className="w-6 h-6 text-navy mx-auto mb-2" strokeWidth={1.5} />
            <div className="font-medium text-navy text-sm mb-0.5">Format</div>
            <div className="text-xs text-slate">Video-Call</div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-black/5 text-center">
            <CheckCircle2 className="w-6 h-6 text-navy mx-auto mb-2" strokeWidth={1.5} />
            <div className="font-medium text-navy text-sm mb-0.5">Kosten</div>
            <div className="text-xs text-slate">Kostenlos</div>
          </div>
        </div>

        <div className="mb-6">
          <p className="text-sm text-charcoal">Wähle einen passenden Termin in meinem Kalender:</p>
        </div>
        
        {/* Embedded Calendly Widget */}
        <div className="bg-white rounded-xl border border-black/5 shadow-sm overflow-hidden mb-8">
          <style>{`
            .calendly-inline-widget iframe {
              overflow: hidden !important;
            }
            .calendly-inline-widget {
              overflow: hidden !important;
            }
          `}</style>
          <div 
            ref={calendlyContainerRef}
            className="calendly-inline-widget" 
            data-url={`${CALENDLY_URL}?hide_gdpr_banner=1&hide_event_type_details=1&background_color=ffffff&text_color=1B2236&primary_color=1B2236`}
            style={{ minWidth: '320px', height: '700px', overflow: 'hidden' }}
            data-testid="calendly-widget"
          />
        </div>

        <div className="text-center text-xs text-slate mb-8">
          <p>Nach der Buchung erhältst du eine Bestätigung per E-Mail.</p>
        </div>
      </section>

      {/* Footer */}
      <Footer variant="light" />
    </ConsultingLayout>
  );
};

export default StrategySession;
